import {
    type Settings as LayoutSettings,
} from '@ant-design/pro-components';
import {Link, RequestConfig, RunTimeLayoutConfig} from '@umijs/max';
import {history} from '@umijs/max';
import React from 'react';
import {
    AvatarDropdown,
    AvatarName, Download,
    SearchInput,
} from '@/components';
import defaultSettings from '../config/defaultSettings';
import {errorConfig} from './requestErrorConfig';
import GlobalErrorBoundary from '@/components/GlobalErrorBoundary';
import {currentUser as queryCurrentUser} from '@/services/system/user';
import {getAuthHeader} from "@/services/common";
import * as Icons from '@ant-design/icons';
import dayjs from "dayjs";
import querystring from "querystring";
import {QuestionCircleOutlined, DownloadOutlined} from "@ant-design/icons";
import {isMicroApp, setHostToken} from '@/qiankun';

export const qiankun = {
    async mount(props: any = {}) {
        // 微应用的 base 路径
        props.base = '/user/micro/etmf';
        props?.onGlobalStateChange?.((state: { token?: string }) => {
            if (state?.token) setHostToken(state.token);
        }, true);
    },
    async unmount(props: any = {}) {
        props?.offGlobalStateChange?.();
        setHostToken('');
    },
};

const excludeUrl = ['/logout', '/auth', '/auth/', '/loadingPage', '/loadingPage/'];
const renderIcon = (data: any[]) => {
    data.forEach((it) => {
        const icons = ICONS.filter((icon: string) => icon === it.icon);
        if (icons && icons.length > 0) {
            const IconComponent = Icons[icons[0]];
            it.icon = <IconComponent/>;
        }
        if (it.parentKey) {
            it.parentKey = [it.parentKey];
        }
        if (it.routes && it.routes.length > 0) {
            renderIcon(it.routes);
        }
    });
};

const authUrl = `${AUTH_URL}/sso/auth?client=etmf&redirect=${REDIRECT_URL}`

const handleAuthorities = (source: any[], target: any[]) => {
    source.forEach(it => {
        if (it.code) {
            target.push(it.code);
        }
        if (it.routes && it.routes.length > 0) {
            handleAuthorities(it.routes, target);
        }
    })
}
// 跳转登录
const redirectToSso = () => {
    if (isMicroApp) return;
    window.open(authUrl, "_self");
};

export async function getInitialState(): Promise<{
    settings?: Partial<LayoutSettings>;
    currentUser?: API.CurrentUser;
    loading?: boolean;
    fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
    menus?: any[];
    authorities?: any[];
}> {
    const {location} = history;
    let settings = JSON.parse(localStorage.getItem('settings') || '{}');
    const navTheme = settings.navTheme || 'light';
    settings = {
        ...(defaultSettings as Partial<LayoutSettings>),
        navTheme,
    };
    const fetchUserInfo = async () => {
        try {
            const {data} = await queryCurrentUser({
                skipErrorHandler: true,
            });
            return data;
        } catch (error: any) {
            if (error) {
                const {code, showType} = error.info;
                if (code === 401 && showType === 9) {
                    redirectToSso();
                }
            }
        }
        return;
    };

    // 如果不是登录页面，执行
    if (!excludeUrl.includes(location.pathname)) {
        const currentUser = await fetchUserInfo();
        if (!isMicroApp) {
            localStorage.setItem('TMF_ACCESS_TOKEN', `{"token": "${getAuthHeader().Token}", "id": "${currentUser?.id}"}`);
        }
        const authorities: string[] = [];
        handleAuthorities(currentUser?.menus || [], authorities);
        return {
            fetchUserInfo,
            currentUser,
            authorities,
            settings,
            menus: currentUser?.menus,
        };
    }
    return {
        fetchUserInfo,
        settings,
    };
}

export const layout: RunTimeLayoutConfig = ({initialState}) => {
    return {
        headerRender: isMicroApp ? false : undefined,
        contentStyle: isMicroApp
            ? {height: 'calc(100vh - 60px)', overflow: 'auto'}
            : undefined,
        menu: {
            request: async (params, defaultMenuData) => {
                if (initialState?.currentUser && initialState.currentUser.id) {
                    const menus = initialState?.currentUser.menus;
                    renderIcon(menus);
                    return menus;
                }
                return [];
            },
        },
        links: [
            <Link key="help" to="/help">
                <QuestionCircleOutlined/>
                <span>帮助中心</span>
            </Link>,
            ...(isMicroApp
                ? [
                    <Link key="download" to="/download">
                        <DownloadOutlined/>
                        <span>下载中心</span>
                    </Link>,
                ]
                : []),
        ],
        actionsRender: () => (isMicroApp ? [] : [<Download key="download"/>]),
        avatarProps: isMicroApp
            ? undefined
            : {
                src: initialState?.currentUser?.avatar || "/icons/avatar.png",
                title: <AvatarName/>,
                render: (_, avatarChildren) => {
                    return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
                },
            },
        waterMarkProps: {
            content: `${initialState?.currentUser?.name} ${dayjs().format("YYYY-MM-DD HH:mm:ss")}`,
        },
        onPageChange: (location: { pathname: string, search: string }) => {
            let search = location?.search;
            if (search) {
                search = search.substring(1);
            }
            const {user, sign, timeStr} = querystring.parse(search)
            if (
                user &&
                sign &&
                timeStr &&
                location.pathname === "/"
            ) {
                history.push(`/loadingPage?user=${encodeURIComponent(user as string)}&sign=${sign}&timeStr=${timeStr}`);
                return;
            }
            if (!initialState?.currentUser && !excludeUrl.includes(location.pathname)) {
                redirectToSso();
            }
            if (location.pathname === "/") {
                const exist = initialState?.menus?.find((it) => it.path === "/home");
                if (!exist && initialState?.menus?.length && initialState?.menus?.length > 0) {
                    let redirect = '/home';
                    const findDefault = (menus: any[]) => {
                        for (let i = 0; i < menus.length; i++) {
                            const menu = menus[i];
                            if (menu.children && menu.children.length > 0) {
                                findDefault(menu.children);
                            } else {
                                if (!redirect) {
                                    redirect = menu.path;
                                }
                                break;
                            }
                        }
                    }
                    findDefault(initialState.menus);
                    history.push(redirect || '/home');
                }
            }
            return;
        },
        menuHeaderRender: (node: any, _: any, props: any) =>
            props?.collapsed ? null : <SearchInput/>,
        // 自定义 403 页面
        // unAccessible: <div>unAccessible</div>,
        childrenRender: (children) => {
            return <GlobalErrorBoundary>{children}</GlobalErrorBoundary>;
        },
        ...initialState?.settings,
        token: {
            sider: {
                colorMenuBackground: "#FFFFFF",
            },
        },
    };
};

export const request: RequestConfig = {
    baseURL: BASE_URL,
    ...errorConfig,
};
